// groupMap.mjs
//
// THE ONE PLACE the mapping between a stable, human-readable, URL-safe facet
// key and the current OpenCart `oc_filter_group.filter_group_id` lives.
// Nothing else in this module (or, once adopted, in catalog-filters.vue /
// category.vue) should hardcode a filter_group_id. If the catalog is
// reorganised in OpenCart admin and a group id changes, this table is the
// only file that needs to change.
//
// Source of the confirmed rows: read-only production `api/filters` and
// OpenCart filter-group inspection on 2026-08-11. This table supports legacy
// migration and known defaults; it is deliberately NOT a rendering
// whitelist. catalogSearchV1 descriptors can publish additional stable keys
// and the generic UI/codec will consume them without a frontend release.
//
// IMPORTANT — facet *type* used here is only "checkbox" | "range", not the
// three-way "checkbox" | "radio" | "range" that filters.php uses. The
// reference evaluator (`dev/reference-evaluator/evaluator.mjs`,
// `validateSelection`) only ever recognises `group.type === "checkbox"` or
// `group.type === "range"` — "radio" does not exist as a distinct semantic
// type in the selection contract the evaluator (and, by extension, the real
// query engine) consumes. In OpenCart, "radio" vs "checkbox" is a UI-only
// cardinality hint (does the widget let you pick one option or several); the
// underlying matching rule is identical OR-within-group either way. So group
// ids 7, 8, 11, 13 (declared "radio" in filters.php) still collapse to
// `kind: "checkbox"` in this table. If the real query engine ever needs to
// *enforce* single-selection server-side, that is a UI/validation concern
// layered on top of `facets`, not a reason to add a third `kind`.
//
// @typedef {"checkbox" | "range"} FacetKind
// @typedef {{
//   key: string,
//   groupId: number,
//   kind: FacetKind,
//   confirmed: boolean,
//   note?: string,
// }} FacetGroupEntry

/** @type {FacetGroupEntry[]} */
export const FACET_GROUPS = [
  // Production source-of-truth snapshot, verified read-only on 2026-08-11.
  // The map exists for legacy URL migration and known defaults. The
  // Orchestrator response is authoritative for labels, values, counts and UI.
  { key: "lens_type", groupId: 1, kind: "checkbox", confirmed: true },
  { key: "lens_width", groupId: 2, kind: "range", confirmed: true },
  { key: "frame_shape", groupId: 3, kind: "checkbox", confirmed: true },
  { key: "made_in", groupId: 4, kind: "checkbox", confirmed: true },
  { key: "frame_material", groupId: 5, kind: "checkbox", confirmed: true },
  { key: "frame_color", groupId: 6, kind: "checkbox", confirmed: true },
  { key: "adjustable_nosepads", groupId: 7, kind: "checkbox", confirmed: true },
  {
    key: "polarized",
    groupId: 8,
    kind: "checkbox",
    confirmed: true,
    note: "Server descriptors are authoritative; Lens Type also has a legacy Polarized option.",
  },
  { key: "lens_color", groupId: 9, kind: "checkbox", confirmed: true },
  {
    key: "temple_length",
    groupId: 10,
    kind: "range",
    confirmed: true,
    note: "Production legacy label is Temple width; storefront terminology is Temple Length.",
  },
  { key: "rx_able", groupId: 11, kind: "checkbox", confirmed: true },
  { key: "bridge_width", groupId: 12, kind: "range", confirmed: true },
  { key: "in_stock", groupId: 13, kind: "checkbox", confirmed: true },
  { key: "brand", groupId: 14, kind: "checkbox", confirmed: true },
  { key: "best_seller", groupId: 15, kind: "checkbox", confirmed: true },
  { key: "final_sale", groupId: 16, kind: "checkbox", confirmed: true },
];

// --- Derived lookup structures (built once, frozen) -----------------------

function buildLookups(entries) {
  const byKey = new Map();
  const byGroupId = new Map();

  for (const entry of entries) {
    if (byKey.has(entry.key)) {
      throw new Error(`groupMap.mjs: duplicate stable key "${entry.key}"`);
    }
    if (byGroupId.has(entry.groupId)) {
      throw new Error(
        `groupMap.mjs: duplicate filter_group_id ${entry.groupId} (keys "${
          byGroupId.get(entry.groupId).key
        }" and "${entry.key}")`
      );
    }
    if (!/^[a-z][a-z0-9_]*$/.test(entry.key)) {
      throw new Error(
        `groupMap.mjs: stable key "${entry.key}" is not URL-safe snake_case`
      );
    }
    byKey.set(entry.key, entry);
    byGroupId.set(entry.groupId, entry);
  }

  return { byKey, byGroupId };
}

const { byKey: GROUP_BY_KEY, byGroupId: GROUP_BY_ID } = buildLookups(FACET_GROUPS);

/** @returns {FacetGroupEntry | undefined} */
export function facetByKey(key) {
  return GROUP_BY_KEY.get(key);
}

/** @returns {FacetGroupEntry | undefined} */
export function facetByGroupId(groupId) {
  return GROUP_BY_ID.get(Number(groupId));
}

export function isKnownFacetKey(key) {
  return GROUP_BY_KEY.has(key);
}

export function checkboxFacetKeys() {
  return FACET_GROUPS.filter((f) => f.kind === "checkbox").map((f) => f.key);
}

export function rangeFacetKeys() {
  return FACET_GROUPS.filter((f) => f.kind === "range").map((f) => f.key);
}
